<script lang="ts">
export const AAlertVariantOptions = {
  accent: 'atabix-alert--accent',
  success: 'atabix-alert--success',
  warning: 'atabix-alert--warning',
  error: 'atabix-alert--error',
} as const

const variants = cva('atabix-alert', {
  variants: {
    variant: AAlertVariantOptions,
  },
  defaultVariants: {
    variant: 'accent',
  },
})

export type AAlertVariants = VariantProps<typeof variants>
</script>

<script lang="ts" setup>
import { cva, type VariantProps } from 'class-variance-authority'
import { AlertCircleOutlineIcon, AlertOutlineIcon, CheckCircleOutlineIcon, InformationOutlineIcon } from 'mdi-vue3'
import { type Component, computed, type HTMLAttributes } from 'vue'

import { AButton, cn, type Testable } from '../../../main'
import type { AButtonProperties } from '../AButton/AButton.vue'

// Props
export type AAlertProperties = Testable & {
  title?: string
  subtitle?: string
  variant?: AAlertVariants['variant']
  action?: AButtonProperties | undefined
  class?: HTMLAttributes['class']
  container?: boolean
  showIcon?: boolean
}

const properties = withDefaults(defineProps<AAlertProperties>(), {
  title: '',
  subtitle: '',
  container: false,
  variant: 'accent',
  action: undefined,
  class: undefined,
  showIcon: true,
})

defineOptions({ inheritAttrs: false })
// End props

// Computed
const classes = computed(() => cn(properties.class, variants({ variant: properties.variant })))

// Script
const iconComponent = computed((): Component | undefined => {
  const icons = {
    accent: InformationOutlineIcon,
    success: CheckCircleOutlineIcon,
    warning: AlertOutlineIcon,
    error: AlertCircleOutlineIcon,
  }
  return icons[properties.variant!] || undefined
})
// End Computed
</script>

<template>
  <div :class="classes" :data-test>
    <div class="atabix-alert__wrapper">
      <component :is="iconComponent" v-if="iconComponent && showIcon" class="atabix-alert__icon" />

      <div class="atabix-alert__content">
        <span v-if="title" class="text-base font-bold">{{ title }}</span>
        <span v-if="subtitle" class="text-sm font-normal">{{ subtitle }}</span>
      </div>
    </div>

    <AButton v-if="action" v-bind="action" class="atabix-alert__button" variant="outline" />
  </div>
</template>

<style lang="scss">
.atabix-alert {
  @apply flex;
  @apply flex-col;
  @apply md:flex-row;
  @apply md:items-center;
  @apply p-4;
  @apply items-start;
  @apply justify-between;
  @apply gap-4;
  @apply border;
  @apply rounded-md;
  @apply text-foreground;

  .atabix-alert__wrapper {
    @apply flex;
    @apply flex-row;
    @apply justify-start;
    @apply gap-x-4;
  }

  .atabix-alert__icon {
    @apply hidden;
    @apply justify-start;
    @apply md:flex;
    @apply md:flex-none;
    @apply size-5;
    @apply mt-0.5;
  }

  .atabix-alert__content {
    @apply flex;
    @apply flex-col;
    @apply gap-1;
  }

  .atabix-alert__button {
    @apply flex;
    @apply justify-end;
  }
}

.atabix-alert--accent {
  @apply bg-background-infoLight;
  @apply border-border-info;

  .atabix-alert__icon {
    @apply text-foreground-info;
  }
}

.atabix-alert--success {
  @apply bg-background-successLight;
  @apply border-border-success;

  .atabix-alert__icon {
    @apply text-foreground-success;
  }
}

.atabix-alert--warning {
  @apply bg-background-warningLight;
  @apply border-border-warning;

  .atabix-alert__icon {
    @apply text-foreground-warning;
  }
}

.atabix-alert--error {
  @apply bg-background-alertLight;
  @apply border-border-alert;

  .atabix-alert__icon {
    @apply text-foreground-alert;
  }
}
</style>
